// urlUtils.ts

import {isClient} from './common'
import type {Request} from 'express'

// Type definition for the URL parameters object
type UrlParams = Record<string, string>

export const paramsFromAttributes = (attributes: NamedNodeMap): UrlParams => {
    const params: UrlParams = {}
    if (attributes?.length !== undefined) {
        // Iterate over the NamedNodeMap
        for (let i = 0; i < attributes.length; i++) {
            if (attributes[i].name === 'class') continue
            const attr = attributes[i] // This is of type Attr
            params[attr.name] = attr.value
        }
    }
    return params
}

/**
 * Combines an origin, path, and query parameters into a full URL.
 * @param origin - The base origin (e.g., 'https://example.com')
 * @param path - The path to append to the origin (e.g., '/api/v1/resource')
 * @param params - An optional object representing query parameters (e.g., { query: 'value' })
 * @returns The full constructed URL as a string.
 */
export const createFullUrl = (
    origin: string,
    path: string,
    params?: Record<string, string>
): string => {
    // Combine origin and path using the URL constructor
    const url = new URL(path, origin)

    // If there are query parameters, append them
    if (params) {
        Object.keys(params).forEach((key) => {
            url.searchParams.append(key, params[key])
        })
    }

    // Return the full URL as a string
    return url.toString()
}

/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/**
 * Get the public‐facing origin, either from the browser or,
 * on the server, by looking at forwarded headers (or falling
 * back to an env var).
 *
 * @param req Optional Express request (only available server‑side)
 */
export const getAppOrigin = (req: Request) => {
    // 1. In the browser, just use window.location
    if (isClient) {
        return window.location.origin
    }

    // 2. On the server, if we have a request, try the standard
    //    X-Forwarded headers that Cloudflare (or any proxy) sets
    const forwardedHost = req?.headers?.['x-forwarded-host']
    const forwardedProto = req?.headers?.['x-forwarded-proto']
    const host = Array.isArray(forwardedHost) ? forwardedHost[0] : forwardedHost
    const proto = Array.isArray(forwardedProto) ? forwardedProto[0] : forwardedProto
    if (typeof host === 'string' && typeof proto === 'string' && host && proto) {
        return `${proto}://${host}`
    }

    // 3. Otherwise, pick up the local fallback env var
    const origin = process.env.APP_ORIGIN
    if (!origin) {
        throw new Error('APP_ORIGIN not set and no forwarded headers present on req')
    }
    return origin
}

/**
 * Checks whether a given URL string includes an origin (i.e., is absolute).
 *
 * @param url - The URL to check
 * @returns `true` if the URL includes a protocol + host; otherwise `false`
 */
export const isUrlWithOrigin = (url: string): boolean => {
    try {
        new URL(url)
        return true
    } catch (e) {
        return false
    }
}

/**
 * Formats an SFRA-style href for use in navigation by stripping origin from full URLs
 * and reattaching the correct base origin for the environment.
 *
 * @param pageRefData - An object with either `sfraUrl` or `url`
 * @returns A fully qualified and environment-aware URL string
 */
export const formatSfraHref = ({
    pageRefData,
    threeLetter,
    appOrigin
}: {
    pageRefData: {sfraUrl?: string; url?: string}
    threeLetter: string
    appOrigin: string
}): string => {
    const isDevelopment = process.env.NODE_ENV === 'development'
    const baseUrl = isDevelopment ? `https://${threeLetter}.devhec.com` : appOrigin

    if (pageRefData?.sfraUrl) {
        const href = pageRefData.sfraUrl

        if (isUrlWithOrigin(href)) {
            const originalUrl = new URL(href)
            return `${baseUrl}${originalUrl.pathname}${originalUrl.search}${originalUrl.hash}`
        } else if (href.startsWith('/') && href.length > 1) {
            return `${baseUrl}${href}`
        }
        return `${baseUrl}/${href}`
    } else {
        return `${baseUrl}${pageRefData?.url as string}`
    }
}
